import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, ChevronDown, LogOut, Check, CheckCircle2, Sun, Moon, Palette, Bug, Menu, Instagram, Command } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAccount } from "@/context/AccountContext";
import { useChatUnread } from "@/lib/chat";
import { useLocation } from "react-router-dom";
import { useTheme, ACCENTS } from "@/context/ThemeContext";
import { BugReportDialog } from "@/components/BugReportDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/UserAvatar";

export function Topbar({ search, onSearch, approvals = [], onMenuClick = () => {} }) {
  const { user, logout } = useAuth();
  const { accounts, current, switchAccount } = useAccount();
  const { mode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const [localSearch, setLocalSearch] = useState(search || "");
  const [bugOpen, setBugOpen] = useState(false);
  const approvalsCount = approvals.length;
  const location = useLocation();
  const chatUnread = useChatUnread(location.pathname === "/chat");

  return (
    <header data-testid="app-topbar" className="app-topbar no-print sticky top-0 z-30 shrink-0 px-3 lg:px-2 pt-[calc(12px+env(safe-area-inset-top))]">
      <div data-testid="channel-accent-line" className="sr-only" style={{ background: current?.accent || "#18181B" }} />
      <div className="liquid-topbar min-h-[62px] flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 min-w-0">
      {/* Burger (mobil) */}
      <button
        data-testid="mobile-menu-button"
        onClick={onMenuClick}
        aria-label="Navigation öffnen"
        className="liquid-icon-button relative lg:hidden h-11 w-11 shrink-0 flex items-center justify-center text-slate-700"
      >
        <Menu className="h-5 w-5" />
        {chatUnread > 0 && (
          <span data-testid="mobile-menu-chat-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {chatUnread > 99 ? "99+" : chatUnread}
          </span>
        )}
      </button>

      {/* Account switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="account-switcher"
            aria-label={`Account wechseln · ${current?.name || "Account"}`}
            className="liquid-control flex h-11 items-center gap-2.5 pl-2.5 pr-3 transition-all hover:bg-white/80"
            style={{
              borderColor: `${current?.accent || "#18181B"}40`,
              background: `${current?.accent || "#18181B"}0d`,
            }}
          >
            <span className="h-8 w-8 rounded-[10px] flex items-center justify-center text-white shadow-sm" style={{ background: current?.accent || "#18181B" }}>
              <Instagram className="h-[18px] w-[18px]" />
            </span>
            <span className="hidden sm:block text-sm font-semibold text-neutral-800 max-w-[180px] truncate">
              {current?.name || "Account"}
            </span>
            <ChevronDown className="hidden sm:block h-4 w-4 text-neutral-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="text-xs text-neutral-400">Account wechseln</DropdownMenuLabel>
          {accounts.map((a) => (
            <DropdownMenuItem
              key={a.key}
              data-testid={`account-option-${a.key}`}
              onClick={() => switchAccount(a.key)}
              className="flex items-start gap-3 py-2.5 cursor-pointer"
            >
              <span className="h-2.5 w-2.5 rounded-full mt-1.5" style={{ background: a.accent }} />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-neutral-800">{a.name}</span>
                <span className="block text-[11px] text-neutral-400">{a.tagline}</span>
              </span>
              {current?.key === a.key && <Check className="h-4 w-4 text-neutral-900 mt-1" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search */}
      <div className="relative flex-1 max-w-[470px] hidden md:block">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-500" />
        <input
          data-testid="global-search-input"
          value={localSearch}
          onChange={(e) => {
            setLocalSearch(e.target.value);
            onSearch?.(e.target.value);
          }}
          placeholder="Themen, Inhalte, Creator suchen…"
          className="liquid-search w-full h-11 pl-11 pr-16 text-[14px] focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 text-[11px] font-semibold text-slate-400"><Command className="h-3 w-3" /> K</span>
      </div>

      <div className="flex-1" />

      {/* Bug melden */}
      <button
        data-testid="bug-report-btn"
        onClick={() => setBugOpen(true)}
        title="Bug melden"
        aria-label="Bug melden"
        className="liquid-icon-button h-11 w-11 shrink-0 hidden sm:flex items-center justify-center text-slate-600"
      >
        <Bug className="h-5 w-5" />
      </button>
      <BugReportDialog open={bugOpen} onOpenChange={setBugOpen} />

      {/* Theme toggle */}
      <button
        data-testid="theme-toggle"
        onClick={toggleMode}
        title={mode === "dark" ? "Heller Modus" : "Dunkler Modus"}
        aria-label={mode === "dark" ? "Hellen Modus aktivieren" : "Dunklen Modus aktivieren"}
        className="liquid-icon-button h-11 w-11 flex items-center justify-center text-slate-600"
      >
        {mode === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      {/* Approvals + Alerts notifications */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="topbar-approvals" aria-label={`${approvalsCount} offene Benachrichtigungen`} className="liquid-icon-button relative h-11 w-11 flex items-center justify-center text-slate-600">
            <Bell className="h-5 w-5" />
            {approvalsCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">
                {approvalsCount}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Benachrichtigungen</span>
            {approvalsCount > 0 && <span className="text-[11px] font-medium text-amber-600">{approvalsCount} offen</span>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {approvalsCount === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-neutral-400">
              <CheckCircle2 className="h-6 w-6 mx-auto text-green-500 mb-1.5" />
              Keine offenen Freigabe-Anfragen
            </div>
          ) : (
            approvals.slice(0, 6).map((c) => (
              <DropdownMenuItem
                key={c.id}
                data-testid={`notif-${c.id}`}
                onClick={() => navigate("/freigabe")}
                className="flex items-start gap-2.5 py-2.5 cursor-pointer"
              >
                <span className="mt-0.5 h-7 w-7 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-neutral-800 truncate">{c.title}</span>
                  <span className="block text-[11px] text-neutral-400">Wartet auf Freigabe · {c.assigned_creator || "—"}</span>
                </span>
              </DropdownMenuItem>
            ))
          )}
          {approvalsCount > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="notif-view-all" onClick={() => navigate("/freigabe")} className="justify-center text-sm font-medium text-blue-600 cursor-pointer">
                Alle im Freigabe-Center öffnen
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-trigger" className="liquid-control flex h-11 shrink-0 items-center gap-2 pl-1.5 pr-3 hover:bg-white/80">
            <UserAvatar src={user?.avatar_url} name={user?.name || "?"} color={user?.avatar_color || "#18181B"} className="h-8 w-8" textClass="text-xs" />
            <ChevronDown className="h-4 w-4 text-neutral-400 hidden sm:block" />
            <span className="hidden xl:block text-sm font-semibold text-slate-700">{user?.role === "admin" ? "Redaktion" : "Creator"}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-sm font-semibold text-neutral-800">{user?.name}</div>
            <div className="text-xs text-neutral-400 font-normal">{user?.email}</div>
            <div className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
              {user?.role === "admin" ? "Redaktion" : "Creator"}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="settings-link" onClick={() => navigate("/einstellungen")} className="cursor-pointer">
            <Palette className="h-4 w-4 mr-2" /> Einstellungen & Integrationen
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600 cursor-pointer">
            <LogOut className="h-4 w-4 mr-2" /> Abmelden
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );
}
